import type { MessageId, ThreadId } from "@t3tools/contracts";
import {
  liveTextForMessage,
  pendingLiveMessages,
  provisionalLiveSequence,
  type LiveMessageOverlay,
} from "@t3tools/client-runtime";
import type { EnvironmentState } from "./store";
import type { ChatMessage, Thread, ThreadSession, ThreadShell, ThreadTurnState } from "./types";

const EMPTY_MESSAGES: ChatMessage[] = [];
const EMPTY_ACTIVITIES: Thread["activities"] = [];
const EMPTY_MESSAGE_MAP: Record<MessageId, ChatMessage> = {};
const EMPTY_ACTIVITY_MAP: Record<string, Thread["activities"][number]> = {};

const collectedByIdsCache = new WeakMap<readonly string[], WeakMap<object, readonly unknown[]>>();
const threadCache = new WeakMap<
  ThreadShell,
  {
    session: ThreadSession | null;
    turnState: ThreadTurnState | undefined;
    messages: Thread["messages"];
    activities: Thread["activities"];
    thread: Thread;
  }
>();

function collectByIds<TKey extends string, TValue>(
  ids: readonly TKey[] | undefined,
  byId: Record<TKey, TValue> | undefined,
  emptyValue: TValue[],
): TValue[] {
  if (!ids || ids.length === 0 || !byId) {
    return emptyValue;
  }

  const cachedByRecord = collectedByIdsCache.get(ids);
  const cached = cachedByRecord?.get(byId);
  if (cached) {
    return cached as TValue[];
  }

  const nextValues = ids.flatMap((id) => {
    const value = byId[id];
    return value ? [value] : [];
  });
  const nextCachedByRecord = cachedByRecord ?? new WeakMap<object, readonly unknown[]>();
  nextCachedByRecord.set(byId, nextValues);
  if (!cachedByRecord) {
    collectedByIdsCache.set(ids, nextCachedByRecord);
  }
  return nextValues;
}

function selectThreadMessages(state: EnvironmentState, threadId: ThreadId): Thread["messages"] {
  return collectByIds(
    state.messageIdsByThreadId[threadId],
    state.messageByThreadId[threadId] ?? EMPTY_MESSAGE_MAP,
    EMPTY_MESSAGES,
  );
}

function selectThreadActivities(state: EnvironmentState, threadId: ThreadId): Thread["activities"] {
  return collectByIds(
    state.activityIdsByThreadId[threadId],
    state.activityByThreadId[threadId] ?? EMPTY_ACTIVITY_MAP,
    EMPTY_ACTIVITIES,
  );
}

const liveThreadCache = new WeakMap<Thread, WeakMap<object, Thread>>();

/**
 * A thread with in-flight assistant text folded into its messages, for reading
 * only. It must never reach `writeThreadState`: live text is transport state,
 * and persisting it into the projected messages would double the text once the
 * durable message arrives, and leave a sequence-less row sorting above the
 * user's own message.
 *
 * The overlay object is replaced whenever live text advances, which is exactly
 * when the merged thread has to be rebuilt; otherwise the cached one keeps its
 * identity so subscribers do not re-render.
 */
export function withLiveMessages(thread: Thread, overlay: LiveMessageOverlay | undefined): Thread {
  if (overlay === undefined || Object.keys(overlay).length === 0) {
    return thread;
  }

  const cachedByOverlay = liveThreadCache.get(thread);
  const cached = cachedByOverlay?.get(overlay);
  if (cached) {
    return cached;
  }

  const pending = pendingLiveMessages(overlay, thread.messages);
  const sequence = provisionalLiveSequence(thread.messages);
  const messages: ChatMessage[] = thread.messages.map((message) => {
    const liveText = liveTextForMessage(overlay, message);
    return liveText === null ? message : { ...message, text: liveText };
  });
  for (const { messageId, entry } of pending) {
    messages.push({
      id: messageId,
      role: "assistant",
      text: entry.text,
      turnId: entry.turnId,
      ...(sequence !== undefined ? { sequence } : {}),
      createdAt: entry.createdAt,
      streaming: true,
    });
  }

  const merged: Thread = { ...thread, messages };
  const nextCachedByOverlay = cachedByOverlay ?? new WeakMap<object, Thread>();
  nextCachedByOverlay.set(overlay, merged);
  if (!cachedByOverlay) {
    liveThreadCache.set(thread, nextCachedByOverlay);
  }
  return merged;
}

export function getThreadFromEnvironmentState(
  state: EnvironmentState,
  threadId: ThreadId,
): Thread | undefined {
  const shell = state.threadShellById[threadId];
  if (!shell) {
    return undefined;
  }

  const session = state.threadSessionById[threadId] ?? null;
  const turnState = state.threadTurnStateById[threadId];
  const messages = selectThreadMessages(state, threadId);
  const activities = selectThreadActivities(state, threadId);
  const cached = threadCache.get(shell);

  if (
    cached &&
    cached.session === session &&
    cached.turnState === turnState &&
    cached.messages === messages &&
    cached.activities === activities
  ) {
    return cached.thread;
  }

  const thread: Thread = {
    ...shell,
    session,
    latestTurn: turnState?.latestTurn ?? null,
    messages,
    activities,
  };

  threadCache.set(shell, {
    session,
    turnState,
    messages,
    activities,
    thread,
  });

  return thread;
}
